import { useState, useEffect, useMemo } from 'react';
import type { PoseTimerState, PoseDisplayState, ScheduleItem } from '@modelcast/types';

/**
 * Calculate total elapsed seconds from a timestamp-based PoseTimerState.
 * Accounts for pause time and skip offsets.
 * Returns null if timer doesn't have timestamp fields.
 */
function calculateElapsedSeconds(timer: PoseTimerState): number | null {
  // If no timestamp fields, return null to indicate we should use legacy values
  if (!timer.startedAt) return null;

  const now = Date.now();
  const startTime = new Date(timer.startedAt).getTime();

  // If paused, use pausedAt instead of now
  const endTime = timer.pausedAt ? new Date(timer.pausedAt).getTime() : now;

  // Active time = wall clock - start - pause time + skip offset
  const accumulatedPauseMs = timer.accumulatedPauseMs ?? 0;
  const skipOffsetMs = timer.skipOffsetMs ?? 0;
  const activeMs = endTime - startTime - accumulatedPauseMs + skipOffsetMs;
  return Math.max(0, Math.floor(activeMs / 1000));
}

/**
 * Calculate current position in the schedule from elapsed time.
 * If totalElapsed is null (no timestamps), uses legacy currentPose value.
 */
function calculateSchedulePosition(timer: PoseTimerState, totalElapsed: number | null): {
  currentIndex: number;
  elapsedInCurrent: number;
  remainingInCurrent: number;
  isComplete: boolean;
} {
  const schedule = timer.schedule || [];

  // If no elapsed time (no timestamps), use legacy values
  if (totalElapsed === null) {
    return {
      currentIndex: timer.currentPose || 0,
      elapsedInCurrent: timer.elapsed || 0,
      remainingInCurrent: timer.remaining || 0,
      isComplete: !timer.isActive,
    };
  }

  if (schedule.length === 0) {
    return { currentIndex: 0, elapsedInCurrent: 0, remainingInCurrent: 0, isComplete: true };
  }

  let accumulated = 0;
  for (let i = 0; i < schedule.length; i++) {
    const item = schedule[i];
    const itemEnd = accumulated + item.duration;

    if (item.duration === 0) {
      // Untimed item - we're here until explicitly skipped
      return {
        currentIndex: i,
        elapsedInCurrent: totalElapsed - accumulated,
        remainingInCurrent: 0,
        isComplete: false,
      };
    }

    if (totalElapsed < itemEnd) {
      // We're in this item
      return {
        currentIndex: i,
        elapsedInCurrent: totalElapsed - accumulated,
        remainingInCurrent: itemEnd - totalElapsed,
        isComplete: false,
      };
    }
    accumulated = itemEnd;
  }

  // Schedule complete - stay on last item with 0 remaining
  const lastIndex = schedule.length - 1;
  return {
    currentIndex: lastIndex,
    elapsedInCurrent: schedule[lastIndex]?.duration || 0,
    remainingInCurrent: 0,
    isComplete: true,
  };
}

/**
 * Initial display state when no pose timer is active.
 */
const initialDisplayState: PoseDisplayState = {
  currentIndex: 0,
  elapsed: 0,
  remaining: 0,
  isComplete: false,
  poseName: '',
  isBreak: false,
  duration: 0,
  totalPoses: 0,
};

/**
 * Hook for calculating pose display state from timestamp-based PoseTimerState.
 * Updates every 100ms for smooth timer display.
 *
 * @param poseTimer - The timestamp-based pose timer state from the server
 * @returns Display state with calculated position, elapsed, remaining, etc.
 */
export function usePoseDisplay(poseTimer: PoseTimerState | null): PoseDisplayState {
  const [displayState, setDisplayState] = useState<PoseDisplayState>(initialDisplayState);

  // Memoize schedule reference to avoid unnecessary effect triggers
  const schedule = useMemo(() => poseTimer?.schedule || [], [poseTimer?.schedule]);

  useEffect(() => {
    // If no pose timer or not active, return initial state
    if (!poseTimer?.isActive) {
      setDisplayState(initialDisplayState);
      return;
    }

    // Calculate initial position immediately
    const updatePosition = () => {
      const totalElapsed = calculateElapsedSeconds(poseTimer);
      const pos = calculateSchedulePosition(poseTimer, totalElapsed);
      const currentItem: ScheduleItem | undefined = schedule[pos.currentIndex];

      // Use legacy values if no timestamp-based calculation available
      const poseName = currentItem?.name ?? poseTimer.poseName ?? '';
      const isBreak = currentItem ? currentItem.type === 'break' : (poseTimer.isBreak ?? false);
      const duration = currentItem?.duration ?? poseTimer.duration ?? 0;

      setDisplayState({
        currentIndex: pos.currentIndex,
        elapsed: pos.elapsedInCurrent,
        remaining: pos.remainingInCurrent,
        isComplete: pos.isComplete,
        poseName,
        isBreak,
        duration,
        totalPoses: schedule.length || poseTimer.totalPoses || 0,
      });
    };

    // Update immediately
    updatePosition();

    // If paused or using legacy timer (no timestamps), don't run interval
    // Legacy timer updates come from pose:tick events
    if (poseTimer.isPaused || !poseTimer.startedAt) {
      return;
    }

    // Update every 100ms for smooth display (timestamp-based only)
    const interval = setInterval(updatePosition, 100);

    return () => clearInterval(interval);
  }, [poseTimer, schedule]);

  return displayState;
}

/**
 * Export initial state for components that need default values.
 */
export { initialDisplayState };
